import * as React from "react";
import {
  Button,
  Checkbox,
  cn,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useTerms } from "@/hooks/useTerms";
import {
  draftRow,
  useAIDraftGenerator,
  type HolidayDraftItem,
  type HolidayListDraft,
} from "@/lib/aiDraft";
import {
  Calendar,
  Check,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import type { HolidayKind, HolidayPayload, HolidaySuggestion, HolidayView } from "@eduviaos/shared";
import {
  useBulkImportHolidaySuggestions,
  useCreateHoliday,
  useDeleteHoliday,
  useHolidaySuggestions,
  useHolidays,
  useUpdateHoliday,
} from "@/hooks/useHolidays";

/**
 * Phase G3.5 FE-2 — IA Holidays page.
 *
 * The authoring surface for tenant-wide and branch-scoped holidays.
 * Backed by the BE-3 `/admin/calendar/...` endpoints (eduviaos-
 * backend PR #117).
 *
 * Surfaces three flows:
 *
 *  1. **List + manual CRUD** — admins author one-off closures
 *     (school events, weather, exam days). Each row supports
 *     edit + delete via a row-level menu. Multi-day closures
 *     show the inclusive date range.
 *
 *  2. **Import from suggestions** — admins pick from the curated
 *     India catalogue (national / regional / religious dates) and
 *     bulk-import them into the current tenant. Idempotent: re-
 *     selecting an already-imported entry surfaces in the
 *     "skipped" toast.
 *
 *  3. **Make-up days** — `is_school_open=true` flips the closure
 *     into a working day (e.g. compensating for a strike day).
 *     The timetable solver and attendance roll-call honour the
 *     flag; the page shows it as a green "Open" pill.
 */

const HOLIDAY_KIND_LABEL: Record<HolidayKind, string> = {
  national: "National",
  regional: "Regional",
  religious: "Religious",
  school_event: "School event",
  exam_day: "Exam day",
  weather: "Weather",
  other: "Other",
};

const HOLIDAY_KIND_PILL: Record<HolidayKind, StatusPillStatus> = {
  national: "active",
  regional: "processing",
  religious: "pending",
  school_event: "active",
  exam_day: "failed",
  weather: "archived",
  other: "archived",
};

const HOLIDAY_KINDS: HolidayKind[] = [
  "national",
  "regional",
  "religious",
  "school_event",
  "exam_day",
  "weather",
  "other",
];

interface HolidayFormState {
  branchId: string;
  name: string;
  description: string;
  kind: HolidayKind;
  startDate: string;
  endDate: string;
  isSchoolOpen: boolean;
}

function draftKind(kind: string): HolidayKind {
  return (HOLIDAY_KINDS as string[]).includes(kind) ? (kind as HolidayKind) : "other";
}

function draftDates(item: HolidayDraftItem): string {
  const start = formatDate(item.start_date);
  return item.end_date && item.end_date !== item.start_date
    ? `${start} – ${formatDate(item.end_date)}`
    : start;
}

function describeHolidayList(draft: HolidayListDraft) {
  return draft.holidays.map((item) => ({
    label: draftDates(item),
    value: `${item.name} · ${HOLIDAY_KIND_LABEL[draftKind(item.kind)]}`,
  }));
}

const EMPTY_FORM: HolidayFormState = {
  branchId: "",
  name: "",
  description: "",
  kind: "other",
  startDate: "",
  endDate: "",
  isSchoolOpen: false,
};

export function HolidaysPage() {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editorTarget, setEditorTarget] = React.useState<HolidayView | null>(null);
  const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<HolidayView | null>(null);
  const [kindFilter, setKindFilter] = React.useState<HolidayKind | "all">("all");
  const [aiHolidays, setAiHolidays] = React.useState<HolidayDraftItem[] | null>(null);
  const generateHolidayList = useAIDraftGenerator("holiday_list");

  const holidays = useHolidays({
    kind: kindFilter === "all" ? null : kindFilter,
  });
  const items = holidays.data?.holidays ?? [];

  const openCreate = () => {
    setEditorTarget(null);
    setEditorOpen(true);
  };

  const openEdit = (holiday: HolidayView) => {
    setEditorTarget(holiday);
    setEditorOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Holidays & calendar</PageHeaderHeading>
            <PageHeaderDescription>
              Mark closures (national, regional, religious, school events) and make-up working days.
              Your timetable and attendance honour every entry — institution-wide rows apply to all
              branches, branch-scoped rows override for that branch only.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <AIDraftButton<HolidayListDraft>
              label="Describe holidays"
              title="Draft holidays"
              description="Paste or describe the dates. You'll pick which ones to add before anything is saved."
              placeholder="e.g. Closed for Diwali 31 Oct to 2 Nov, Christmas, and the last Saturday of every month in 2026"
              generate={generateHolidayList}
              describe={describeHolidayList}
              onApply={(draft) => setAiHolidays(draft.holidays)}
              data-testid="holidays-ai-draft"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSuggestionsOpen(true)}
              data-testid="holidays-import-cta"
            >
              <Sparkles aria-hidden="true" className="size-4" />
              Import from catalogue
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="holidays-create-cta">
              <Plus aria-hidden="true" className="size-4" />
              New holiday
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <DataTable>
        <DataTableToolbar>
          <div className="text-muted-foreground text-xs">
            {holidays.isLoading
              ? "Loading…"
              : `${items.length} holiday${items.length === 1 ? "" : "s"}`}
          </div>
          <DataTableToolbarSpacer />
          <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as HolidayKind | "all")}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="holidays-kind-filter">
              <SelectValue placeholder="Filter by kind" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {HOLIDAY_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {HOLIDAY_KIND_LABEL[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DataTableToolbar>
        <DataTableScroll>
          <Table>
            <caption className="sr-only">Holidays</caption>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>School open?</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            {holidays.isLoading ? (
              <DataTableSkeleton columns={6} rows={4} />
            ) : (
              <TableBody>
                {items.map((holiday) => (
                  <HolidayRow
                    key={holiday.id}
                    holiday={holiday}
                    onEdit={() => openEdit(holiday)}
                    onDelete={() => setDeleteTarget(holiday)}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>
        {!holidays.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Calendar className="size-6" aria-hidden="true" />}
            title="No holidays authored yet"
            description="Add a one-off closure with the New holiday button, or pull a curated India calendar via Import from catalogue. Your timetable and attendance respect every entry."
          />
        ) : null}
        <DataTableFooter>
          <div />
        </DataTableFooter>
      </DataTable>

      <HolidayEditor open={editorOpen} onOpenChange={setEditorOpen} target={editorTarget} />
      <SuggestionsSheet open={suggestionsOpen} onOpenChange={setSuggestionsOpen} />
      <AIHolidayReviewSheet holidays={aiHolidays} onClose={() => setAiHolidays(null)} />
      <DeleteConfirmDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────

interface HolidayRowProps {
  holiday: HolidayView;
  onEdit: () => void;
  onDelete: () => void;
}

function HolidayRow({ holiday, onEdit, onDelete }: HolidayRowProps) {
  return (
    <TableRow data-testid={`holiday-row-${holiday.id}`}>
      <TableCell>
        <div className="font-medium">{holiday.name}</div>
        {holiday.description ? (
          <div className="text-muted-foreground line-clamp-1 text-xs">{holiday.description}</div>
        ) : null}
      </TableCell>
      <TableCell>
        <StatusPill
          status={HOLIDAY_KIND_PILL[holiday.kind]}
          label={HOLIDAY_KIND_LABEL[holiday.kind]}
        />
      </TableCell>
      <TableCell>
        <div className="text-muted-foreground text-xs">
          <time dateTime={holiday.start_date}>{formatDate(holiday.start_date)}</time>
          {holiday.end_date && holiday.end_date !== holiday.start_date ? (
            <> – {formatDate(holiday.end_date)}</>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <span className="text-muted-foreground text-xs">
          {holiday.branch_id ? holiday.branch_id : "Institution-wide"}
        </span>
      </TableCell>
      <TableCell>
        {holiday.is_school_open ? (
          <StatusPill status="active" label="Open" />
        ) : (
          <span className="text-muted-foreground text-xs">Closed</span>
        )}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Open menu for ${holiday.name}`}>
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onEdit();
              }}
            >
              <Pencil className="size-4" aria-hidden="true" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onDelete();
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

// ── Editor sheet ─────────────────────────────────────────────────────

interface HolidayEditorProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  target: HolidayView | null;
}

function HolidayEditor({ open, onOpenChange, target }: HolidayEditorProps) {
  const { ct } = useTerms();
  const [form, setForm] = React.useState<HolidayFormState>(EMPTY_FORM);
  const [error, setError] = React.useState<string | null>(null);
  const create = useCreateHoliday();
  const update = useUpdateHoliday();
  const isEdit = target !== null;
  const pending = create.isPending || update.isPending;
  const generateHolidayDraft = useAIDraftGenerator("holiday_list");
  const describeSingleHoliday = React.useCallback((draft: HolidayListDraft) => {
    const first = draft.holidays[0];
    if (!first) return [];
    return [
      ...draftRow("Name", first.name),
      ...draftRow("Dates", draftDates(first)),
      ...draftRow("Kind", HOLIDAY_KIND_LABEL[draftKind(first.kind)]),
      ...draftRow("Description", first.description),
    ];
  }, []);
  const applySingleHoliday = React.useCallback((draft: HolidayListDraft) => {
    const first = draft.holidays[0];
    if (!first) return;
    setForm((prev) => ({
      ...prev,
      name: first.name,
      description: first.description ?? prev.description,
      kind: draftKind(first.kind),
      startDate: first.start_date,
      endDate: first.end_date && first.end_date !== first.start_date ? first.end_date : "",
    }));
    setError(null);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    create.reset();
    update.reset();
    if (target) {
      setForm({
        branchId: target.branch_id ?? "",
        name: target.name,
        description: target.description ?? "",
        kind: target.kind,
        startDate: target.start_date,
        endDate: target.end_date ?? "",
        isSchoolOpen: target.is_school_open,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    // create/update.reset are stable; target identity drives the
    // form reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.id]);

  const validate = (): string | null => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.startDate) return "Start date is required.";
    if (form.endDate && form.endDate < form.startDate) {
      return "End date must be on or after the start date.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);

    const payload: HolidayPayload = {
      branch_id: form.branchId.trim() || null,
      name: form.name.trim(),
      description: form.description.trim() || null,
      kind: form.kind,
      start_date: form.startDate,
      end_date: form.endDate || null,
      is_school_open: form.isSchoolOpen,
    };

    try {
      if (isEdit && target) {
        await update.mutateAsync({ holidayId: target.id, payload });
        toast.success(`Saved changes to "${payload.name}".`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Created holiday "${payload.name}".`);
      }
      onOpenChange(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to save the holiday.";
      setError(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-md"
        data-testid="holiday-editor"
      >
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit holiday" : "New holiday"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? "Update the closure window, scope, or kind. Changes apply to the next schedule generation and attendance check."
              : "Mark a single-day or multi-day closure. Leave Branch ID blank to apply institution-wide."}
          </SheetDescription>
          {isEdit ? null : (
            <div className="pt-2">
              <AIDraftButton<HolidayListDraft>
                title="Draft a holiday"
                placeholder="e.g. Closed 31 Oct to 2 Nov for Diwali"
                generate={generateHolidayDraft}
                describe={describeSingleHoliday}
                onApply={applySingleHoliday}
                disabled={pending}
                data-testid="hl-ai-draft"
              />
            </div>
          )}
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="hl-name">Name</Label>
            <Input
              id="hl-name"
              placeholder="e.g. Diwali"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              maxLength={160}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hl-description">Description (optional)</Label>
            <Textarea
              id="hl-description"
              placeholder={ct("Notes for staff and parents.")}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              maxLength={2000}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hl-kind">Kind</Label>
            <Select
              value={form.kind}
              onValueChange={(v) => setForm((p) => ({ ...p, kind: v as HolidayKind }))}
            >
              <SelectTrigger id="hl-kind" data-testid="hl-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOLIDAY_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {HOLIDAY_KIND_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="hl-start">Start date</Label>
              <Input
                id="hl-start"
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hl-end">End date (optional)</Label>
              <Input
                id="hl-end"
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hl-branch">Branch ID (leave blank for institution-wide)</Label>
            <Input
              id="hl-branch"
              placeholder="branch-default"
              value={form.branchId}
              onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
              maxLength={64}
            />
          </div>
          <div className="border-border/60 flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="hl-school-open" className="text-sm">
                School stays open
              </Label>
              <p className="text-muted-foreground text-xs">
                Flip on for make-up classes (e.g. a Sunday class compensating for a strike day).
              </p>
            </div>
            <Switch
              id="hl-school-open"
              checked={form.isSchoolOpen}
              onCheckedChange={(v) => setForm((p) => ({ ...p, isSchoolOpen: Boolean(v) }))}
            />
          </div>

          {error ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
              data-testid="hl-error"
            >
              {error}
            </div>
          ) : null}
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            <X aria-hidden="true" className="size-4" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending} data-testid="hl-save">
            {pending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Check aria-hidden="true" className="size-4" />
            )}
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create holiday"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Suggestions sheet ────────────────────────────────────────────────

interface SuggestionsSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

function SuggestionsSheet({ open, onOpenChange }: SuggestionsSheetProps) {
  const suggestions = useHolidaySuggestions();
  const bulkImport = useBulkImportHolidaySuggestions();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [branchId, setBranchId] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    bulkImport.reset();
    // bulkImport.reset is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const grouped = React.useMemo(
    () => groupByKind(suggestions.data?.suggestions ?? []),
    [suggestions.data?.suggestions],
  );

  const handleImport = async () => {
    if (selected.size === 0) return;
    try {
      const response = await bulkImport.mutateAsync({
        branch_id: branchId.trim() || null,
        suggestion_ids: Array.from(selected),
      });
      const imported = response.imported.length;
      const skipped = response.skipped_suggestion_ids.length;
      const unknown = response.unknown_suggestion_ids.length;
      const tail = unknown > 0 ? ` · unknown ${unknown}` : "";
      toast.success(`Imported ${imported} · skipped ${skipped}${tail}.`);
      // Reset selection so a second click doesn't duplicate the
      // intent — already-imported rows surface in the skipped tally.
      setSelected(new Set());
    } catch {
      // The mutation hook surfaces the error on `bulkImport.error`.
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        data-testid="holidays-suggestions-sheet"
      >
        <SheetHeader>
          <SheetTitle>Import from catalogue</SheetTitle>
          <SheetDescription>
            Curated India holiday calendar. Selected entries become branch-scoped or
            institution-wide rows you can later edit. Re-importing a row is safe — it won't create
            duplicates.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="hl-import-branch">Branch ID (leave blank for institution-wide)</Label>
            <Input
              id="hl-import-branch"
              placeholder="branch-default"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              maxLength={64}
            />
          </div>

          {suggestions.isLoading ? (
            <div className="text-muted-foreground py-12 text-center text-sm">
              <Loader2 aria-hidden="true" className="mx-auto mb-2 size-5 animate-spin" />
              Loading catalogue…
            </div>
          ) : (
            <div className="space-y-4">
              {(Object.keys(grouped) as HolidayKind[]).map((kind) => {
                const rows = grouped[kind] ?? [];
                if (rows.length === 0) return null;
                return (
                  <section key={kind} className="space-y-2">
                    <h3 className="text-foreground text-xs font-semibold uppercase tracking-wide">
                      {HOLIDAY_KIND_LABEL[kind]}
                    </h3>
                    <ul className="space-y-1">
                      {rows.map((row) => {
                        const isOn = selected.has(row.id);
                        return (
                          <li
                            key={row.id}
                            className={cn(
                              "border-border/60 flex items-start gap-3 rounded-md border p-2",
                              isOn && "border-primary/60 bg-primary/5",
                            )}
                          >
                            <Checkbox
                              checked={isOn}
                              onCheckedChange={() => toggle(row.id)}
                              aria-label={`Select ${row.name}`}
                              data-testid={`hl-suggestion-${row.id}`}
                            />
                            <div className="flex-1 space-y-0.5">
                              <div className="text-sm font-medium">{row.name}</div>
                              <div className="text-muted-foreground text-xs">
                                <time dateTime={row.start_date}>{formatDate(row.start_date)}</time>
                                {row.end_date && row.end_date !== row.start_date ? (
                                  <> – {formatDate(row.end_date)}</>
                                ) : null}
                                {row.region_hint ? <> · {row.region_hint}</> : null}
                              </div>
                              <div className="text-muted-foreground/80 text-xs">
                                {row.description}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}

          {bulkImport.error ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
            >
              {bulkImport.error.message}
            </div>
          ) : null}
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={bulkImport.isPending}
          >
            <X aria-hidden="true" className="size-4" />
            Close
          </Button>
          <Button
            onClick={handleImport}
            disabled={selected.size === 0 || bulkImport.isPending}
            data-testid="hl-import-confirm"
          >
            {bulkImport.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Sparkles aria-hidden="true" className="size-4" />
            )}
            Import {selected.size > 0 ? `(${selected.size})` : ""}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── AI draft review sheet ────────────────────────────────────────────

interface AIHolidayReviewSheetProps {
  holidays: HolidayDraftItem[] | null;
  onClose: () => void;
}

/** Second review step for a drafted holiday list: the admin picks which rows
 *  to add and only "Add" creates them, one `POST` per holiday. */
function AIHolidayReviewSheet({ holidays, onClose }: AIHolidayReviewSheetProps) {
  const create = useCreateHoliday();
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [branchId, setBranchId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const open = holidays !== null;
  const rows = React.useMemo(() => holidays ?? [], [holidays]);

  React.useEffect(() => {
    if (!holidays) return;
    setSelected(new Set(holidays.map((_, index) => index)));
    setError(null);
  }, [holidays]);

  const toggle = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    let added = 0;
    const failed: string[] = [];
    for (const index of Array.from(selected).sort((a, b) => a - b)) {
      const item = rows[index];
      if (!item) continue;
      try {
        await create.mutateAsync({
          branch_id: branchId.trim() || null,
          name: item.name,
          description: item.description,
          kind: draftKind(item.kind),
          start_date: item.start_date,
          end_date: item.end_date,
          is_school_open: false,
        });
        added += 1;
      } catch {
        failed.push(item.name);
      }
    }
    setSaving(false);
    if (failed.length > 0) {
      setError(`Couldn't add ${failed.join(", ")}. The rest were added.`);
      if (added > 0) toast.success(`Added ${added} holiday${added === 1 ? "" : "s"}.`);
      return;
    }
    toast.success(`Added ${added} holiday${added === 1 ? "" : "s"}.`);
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(next) => (next || saving ? null : onClose())}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        data-testid="hl-ai-review-sheet"
      >
        <SheetHeader>
          <SheetTitle>Review drafted holidays</SheetTitle>
          <SheetDescription>
            Untick anything you don&apos;t want. Nothing is added until you press Add.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="hl-ai-branch">Branch ID (leave blank for institution-wide)</Label>
            <Input
              id="hl-ai-branch"
              placeholder="branch-default"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              maxLength={64}
            />
          </div>
          <ul className="space-y-1">
            {rows.map((row, index) => {
              const isOn = selected.has(index);
              return (
                <li
                  key={`${row.name}-${row.start_date}`}
                  className={cn(
                    "border-border/60 flex items-start gap-3 rounded-md border p-2",
                    isOn && "border-primary/60 bg-primary/5",
                  )}
                >
                  <Checkbox
                    checked={isOn}
                    onCheckedChange={() => toggle(index)}
                    aria-label={`Select ${row.name}`}
                  />
                  <div className="flex-1 space-y-0.5">
                    <div className="text-sm font-medium">{row.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {draftDates(row)} · {HOLIDAY_KIND_LABEL[draftKind(row.kind)]}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {error ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
            >
              {error}
            </div>
          ) : null}
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleAdd()}
            disabled={selected.size === 0 || saving}
            data-testid="hl-ai-review-add"
          >
            {saving ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
            Add {selected.size > 0 ? `(${selected.size})` : ""}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Delete confirm ───────────────────────────────────────────────────

interface DeleteConfirmDialogProps {
  target: HolidayView | null;
  onClose: () => void;
}

function DeleteConfirmDialog({ target, onClose }: DeleteConfirmDialogProps) {
  const remove = useDeleteHoliday();

  const handleDelete = async () => {
    if (!target) return;
    try {
      await remove.mutateAsync({ holidayId: target.id });
      toast.success(`Deleted "${target.name}".`);
      onClose();
    } catch {
      // The mutation surface keeps the error visible on
      // `remove.error`; the dialog stays open so the user can retry.
    }
  };

  return (
    <Dialog open={target !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-testid="hl-delete-confirm">
        <DialogHeader>
          <DialogTitle>Delete this holiday?</DialogTitle>
          <DialogDescription>
            {target ? (
              <>
                <strong>{target.name}</strong> on{" "}
                <time dateTime={target.start_date}>{formatDate(target.start_date)}</time> will be
                permanently removed. Your timetable and attendance will treat the day as a normal
                working day on the next run.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {remove.error ? (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
          >
            {remove.error.message}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={remove.isPending}
            data-testid="hl-delete-confirm-button"
          >
            {remove.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Trash2 aria-hidden="true" className="size-4" />
            )}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function groupByKind(items: HolidaySuggestion[]): Record<HolidayKind, HolidaySuggestion[]> {
  const out = {} as Record<HolidayKind, HolidaySuggestion[]>;
  for (const kind of HOLIDAY_KINDS) out[kind] = [];
  for (const item of items) {
    const bucket = out[item.kind] ?? (out[item.kind] = []);
    bucket.push(item);
  }
  for (const kind of HOLIDAY_KINDS) {
    out[kind].sort((a, b) => a.start_date.localeCompare(b.start_date));
  }
  return out;
}

function formatDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
